import classNames from 'classnames';
import { useCallback, useEffect, useMemo } from 'react';
import { useState } from 'react';
import Marquee from 'react-fast-marquee';
import { useFormContext } from 'react-hook-form';

import Card from '../../../components/Card';
import CheckBoxGroup from '../../../components/form/CheckBoxGroup';
import ColorPickerDropdown from '../../../components/form/ColorPickerDropdown';
import Input from '../../../components/form/Input';
import ToggleThemeButton from '../../../components/ToggleThemeButton';
import DeeplinkSectionItem from '../components/DeeplinkSectionItem';
import ExploreSectionTarget from '../components/ExploreSectionTarget';
import ProductSetSectionTarget from '../components/ProductSetSectionTarget';
import SearchSectionTarget from '../components/SearchSectionTarget';
import { IPropsShortcutItem } from '../components/ShortcutSectionItem';
import { ISectionLayoutId, ISectionTargetType, TARGET_OPTION_BY_LAYOUT_ID, IConfigItemType } from '../validates/sectionBuilderSchema';

const getInputPropsByTargetType = (targetType?: ISectionTargetType) => {
  switch (targetType) {
    case ISectionTargetType.FEED:
      return { name: 'target.slug', label: 'slug', placeholder: 'slug' };
    case ISectionTargetType.LINK:
      return { name: 'target.url', label: 'url', placeholder: 'url' };
    case ISectionTargetType.USER:
      return { name: 'target._id', label: '_id', placeholder: '_id' };
    default: {
      return { name: '', label: '-' };
    }
  }
};

const SectionDetailWithTargetPresenter = () => {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();

  const [announcementText, target, layoutId, configItems, bannerFullWidthImageUrl] = watch(['announcementText', 'target', 'layoutId', 'configItems', 'bannerFullWidthImageUrl']);
  const isMarqueeAnnouncement = layoutId === ISectionLayoutId.MarqueeAnnouncement;
  const isBannerFullWidth = layoutId === ISectionLayoutId.BannerFullWidth;
  // Remove local state, use form value for gradientMode
  const [gradientMode] = watch(['gradientMode']);
  // Track whether we've already synced gradientMode based on initial colors
  const [hasInitializedGradientMode, setHasInitializedGradientMode] = useState(false);
  // Watch color fields for controlled color pickers
  const [textLightColorHexcode, textDarkColorHexcode, bgLightColorHexcode1, bgDarkColorHexcode1, bgLightColorHexcode2, bgDarkColorHexcode2] = watch([
    'textLightColorHexcode',
    'textDarkColorHexcode',
    'bgLightColorHexcode1',
    'bgDarkColorHexcode1',
    'bgLightColorHexcode2',
    'bgDarkColorHexcode2',
  ]);
  const [shortcutType, setShortcutType] = useState<IConfigItemType | null>(configItems?.[0]?.type);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);

  // ------------------------------------------------------------
  // Auto-check gradient mode (edit mode)
  // If the left background colors for light & dark themes differ,
  // it implies that gradient mode was enabled when the section was
  // previously saved. We only run this logic once on first render
  // to avoid overriding any subsequent user interaction.
  useEffect(() => {
    if (isMarqueeAnnouncement && !hasInitializedGradientMode && bgLightColorHexcode1 && bgDarkColorHexcode1) {
      if (bgLightColorHexcode1 !== bgDarkColorHexcode1) {
        setValue('gradientMode', true);
      }
      setHasInitializedGradientMode(true);
    }
  }, [isMarqueeAnnouncement, hasInitializedGradientMode, bgLightColorHexcode1, bgDarkColorHexcode1, setValue]);

  useEffect(() => {
    if (target?.type === ISectionTargetType.DEEP_LINK && configItems?.[0]?.type && configItems?.[0]?.type !== shortcutType) {
      setShortcutType(configItems?.[0]?.type);
    }
  }, [configItems, shortcutType, target?.type]);

  useEffect(() => {
    if (target?.type && !TARGET_OPTION_BY_LAYOUT_ID[layoutId as ISectionLayoutId].some((option) => option.value === target.type)) {
      setValue('target', null);
    }
  }, [layoutId, setValue, target?.type]);

  const renderTargetInput = useCallback(
    (type?: ISectionTargetType) => {
      switch (type) {
        case ISectionTargetType.FEED:
        case ISectionTargetType.LINK:
        case ISectionTargetType.USER:
          return (
            <div>
              <Input register={register} {...getInputPropsByTargetType(target?.type)} />
            </div>
          );
        case ISectionTargetType.SEARCH:
          return (
            <div>
              <SearchSectionTarget register={register} />
            </div>
          );
        case ISectionTargetType.EXPLORE:
        case ISectionTargetType.EXPLORE_BUNDLES:
          return (
            <div>
              <ExploreSectionTarget register={register} />
            </div>
          );
        case ISectionTargetType.PRODUCT_SET:
          return (
            <div>
              <ProductSetSectionTarget />
            </div>
          );
        default:
          return null;
      }
    },
    [register, target?.type],
  );

  const targetOptions = useMemo(() => {
    return TARGET_OPTION_BY_LAYOUT_ID[layoutId as ISectionLayoutId];
  }, [layoutId]);

  // Provide default colors when the user hasn't selected any
  const textColor = isDarkMode ? textDarkColorHexcode : textLightColorHexcode;
  const bgColor1 = isDarkMode ? bgDarkColorHexcode1 : bgLightColorHexcode1;
  const bgColor2 = isDarkMode ? bgDarkColorHexcode2 : bgLightColorHexcode2;
  const background = gradientMode ? `linear-gradient(to right, ${bgColor1}, ${bgColor2})` : bgColor1;

  return (
    <Card className="space-y-16">
      <div className="text-22">Section Detail</div>
      {isMarqueeAnnouncement && (
        <Input
          name="announcementText"
          register={register}
          label="ข้อความประกาศ (Announcement Text)"
          required
          placeholder="Enter announcement text"
          helperText={errors.announcementText?.message as string}
          isInvalid={!!errors.announcementText}
        />
      )}

      {isBannerFullWidth && (
        <div className="space-y-8">
          <Input
            name="bannerFullWidthImageUrl"
            register={register}
            label="Image URL"
            placeholder="https://banner-full-width.png"
            required
            helperText={errors.bannerFullWidthImageUrl?.message as string}
            isInvalid={!!errors.bannerFullWidthImageUrl}
          />

          <div className="rounded-[6px] border border-dashed bg-[#FFEAEA] p-4 overflow-auto">
            {bannerFullWidthImageUrl ? (
              <div className="mx-auto w-[1170px] overflow-hidden rounded-12">
                <img src={bannerFullWidthImageUrl} alt="Banner Full Width Preview" className="h-auto max-h-[480px] w-full object-cover" />
              </div>
            ) : (
              <div className="flex min-h-[120px] items-center justify-center">
                <p className="px-16 text-center font-semibold text-colorsBrandRed01Primary">Image Preview</p>
              </div>
            )}
          </div>
          <p className="text-sm text-systemGrays02LabelSecondary">แนะนำขนาดรูปภาพ: ความกว้างสูงสุด 1170px · ความสูงสูงสุด 480px · ควรวางข้อความและองค์ประกอบสำคัญไว้ตรงกลางภาพ</p>
        </div>
      )}

      {/* Marquee Announcement Fields */}
      {isMarqueeAnnouncement && (
        <div className="space-y-8 mt-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-24 gap-y-4">
            <div>
              <ColorPickerDropdown required value={textLightColorHexcode || ''} onChange={(color) => setValue('textLightColorHexcode', color)} label="สีข้อความ (โหมดสว่าง)" />
              {errors.textLightColorHexcode && (
                <label className="pt-2 label">
                  <span className="label-text-alt text-colorsBrandRed01Primary">{errors.textLightColorHexcode.message as string}</span>
                </label>
              )}
            </div>
            <div>
              <ColorPickerDropdown required value={textDarkColorHexcode || ''} onChange={(color) => setValue('textDarkColorHexcode', color)} label="สีข้อความ (โหมดมืด)" />
              {errors.textDarkColorHexcode && (
                <label className="pt-2 label">
                  <span className="label-text-alt text-colorsBrandRed01Primary">{errors.textDarkColorHexcode.message as string}</span>
                </label>
              )}
            </div>
            <div>
              <ColorPickerDropdown
                required
                value={bgLightColorHexcode1 || ''}
                onChange={(color) => setValue('bgLightColorHexcode1', color)}
                label={gradientMode ? 'สีพื้นหลังฝั่งซ้าย (โหมดสว่าง)' : 'สีพื้นหลัง (โหมดสว่าง)'}
              />
              {errors.bgLightColorHexcode1 && (
                <label className="pt-2 label">
                  <span className="label-text-alt text-colorsBrandRed01Primary">{errors.bgLightColorHexcode1.message as string}</span>
                </label>
              )}
            </div>
            <div>
              <ColorPickerDropdown
                required
                value={bgDarkColorHexcode1 || ''}
                onChange={(color) => setValue('bgDarkColorHexcode1', color)}
                label={gradientMode ? 'สีพื้นหลังฝั่งซ้าย (โหมดมืด)' : 'สีพื้นหลัง (โหมดมืด)'}
              />
              {errors.bgDarkColorHexcode1 && (
                <label className="pt-2 label">
                  <span className="label-text-alt text-colorsBrandRed01Primary">{errors.bgDarkColorHexcode1.message as string}</span>
                </label>
              )}
            </div>
          </div>
          <div className="mt-4">
            <CheckBoxGroup name="gradientMode" register={register} options={[{ label: 'ไล่เฉดสี (ซ้ายไปขวา)', value: true }]} />
          </div>
          {gradientMode && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-24 gap-y-4 mt-4">
              <div>
                <ColorPickerDropdown
                  required={gradientMode}
                  value={bgLightColorHexcode2 || ''}
                  onChange={(color) => setValue('bgLightColorHexcode2', color)}
                  label="สีพื้นหลังฝั่งขวา (โหมดสว่าง)"
                />
                {errors.bgLightColorHexcode2 && (
                  <label className="pt-2 label">
                    <span className="label-text-alt text-colorsBrandRed01Primary">{errors.bgLightColorHexcode2.message as string}</span>
                  </label>
                )}
              </div>
              <div>
                <ColorPickerDropdown
                  required={gradientMode}
                  value={bgDarkColorHexcode2 || ''}
                  onChange={(color) => setValue('bgDarkColorHexcode2', color)}
                  label="สีพื้นหลังฝั่งขาว (โหมดมืด)"
                />
                {errors.bgDarkColorHexcode2 && (
                  <label className="pt-2 label">
                    <span className="label-text-alt text-colorsBrandRed01Primary">{errors.bgDarkColorHexcode2.message as string}</span>
                  </label>
                )}
              </div>
            </div>
          )}
          {announcementText && (
            <div className="flex">
              <div style={{ background, width: '100%' }}>
                <Marquee gradient={false}>
                  <p style={{ color: textColor, padding: '10px' }}>{announcementText}</p>
                </Marquee>
              </div>
              {/* implement toggle dark mode */}
              <ToggleThemeButton className="w-[100px]" currentTheme={isDarkMode ? 'NIGHT' : 'DAY'} toggleTheme={() => setIsDarkMode(!isDarkMode)} />
            </div>
          )}
        </div>
      )}

      <div className="space-y-8">
        <label className="label">
          <span className="label-text text-systemGrays02LabelSecondary">Target Type</span>
        </label>
        <div className="space-x-4">
          {targetOptions.map((option) => (
            <button
              type="button"
              key={option.label}
              className={classNames('btn btn-outline border', {
                'border-colorsBrandRed01Primary text-colorsBrandRed01Primary': target?.type === option.value || (!target?.type && option.label === 'ไม่ระบุ'),
              })}
              onClick={() => setValue('target', option?.value ? { type: option.value } : null)}
            >
              {option.label}
            </button>
          ))}
          {renderTargetInput(target?.type)}
        </div>
      </div>

      {/* Target type selector (none/link) already rendered above */}
      {target?.type === ISectionTargetType.DEEP_LINK && (
        <div className="space-y-12">
          <label className="label text-systemGrays02LabelSecondary">Link Target</label>
          <select
            className="select select-bordered w-fit"
            value={shortcutType || ''}
            onChange={(e) => {
              const newType = e.target.value as IConfigItemType;
              setShortcutType(newType);
              setValue('configItems', [
                {
                  type: newType,
                },
              ]); // Reset configItems to only the selected type
            }}
          >
            <option value="">เลือก Link Target</option>
            <option value={IConfigItemType.EbookShortcut}>Ebook</option>
            <option value={IConfigItemType.EbookBundleShortcut}>Ebook Bundle</option>
            <option value={IConfigItemType.ProductSetShortcut}>Product Set</option>
            <option value={IConfigItemType.LinkShortcut}>Link</option>
            <option value={IConfigItemType.SearchShortcut}>Search</option>
            <option value={IConfigItemType.ExploreShortcut}>Explore</option>
            <option value={IConfigItemType.ExploreBundleShortcut}>Explore Bundle</option>
            <option value={IConfigItemType.PintoFeedShortcut}>Feed</option>
            <option value={IConfigItemType.UserProfileShortcut}>User Profile</option>
            <option value={IConfigItemType.EbookCollectionShortcut}>Ebook Collection</option>
            <option value={IConfigItemType.ExploreEbookCollectionShortcut}>Explore Ebook Collection</option>
            <option value={IConfigItemType.PintoCustomLink}>Custom Link</option>
          </select>
          {/* Show shortcut input UI if a type is selected */}
          {shortcutType &&
            [
              IConfigItemType.EbookShortcut,
              IConfigItemType.EbookBundleShortcut,
              IConfigItemType.ProductSetShortcut,
              IConfigItemType.LinkShortcut,
              IConfigItemType.SearchShortcut,
              IConfigItemType.ExploreShortcut,
              IConfigItemType.ExploreBundleShortcut,
              IConfigItemType.PintoFeedShortcut,
              IConfigItemType.UserProfileShortcut,
              IConfigItemType.EbookCollectionShortcut,
              IConfigItemType.ExploreEbookCollectionShortcut,
              IConfigItemType.PintoCustomLink,
            ].includes(shortcutType) && <DeeplinkSectionItem index={0} itemData={{ type: shortcutType, ...configItems?.[0] } as IPropsShortcutItem} register={register} />}
        </div>
      )}
    </Card>
  );
};

export default SectionDetailWithTargetPresenter;
